import imageCompression from "browser-image-compression";
import React, { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ReactComponent as ImageIcon } from "../../../../assets/images/ImageBlack.svg";
import Button from "../../../../components/essentials/button/Button";
import UserCard from "../../../../components/essentials/user-card/UserCard";
import { selectUser } from "../../../../core/auth/authSlice";
import { User } from "../../../../shared/models";
import http from "../../../../shared/utils/http";
import "./CreatePost.scss";

export interface props {
	closeModal: any;
	onCreatePost: any;
}

const CreatePost: React.FC<props> = ({ closeModal, onCreatePost }: props) => {
	const { t } = useTranslation();
	const { control, handleSubmit, register } = useForm({
		defaultValues: {
			description: "",
		},
	});

	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [compressedFile, setCompressedFile] = useState<File | null>(null);
	const [isCompressing, setIsCompressing] = useState(false);
	const previewBlobUrlRef = useRef<string | null>(null);

	// Watch the description field to enable/disable button
	const description = useWatch({
		control,
		name: "description",
	});

	// Allow submit when there's text OR media attached, and not still compressing
	const hasContent = (description?.trim().length > 0 || !!compressedFile) && !isCompressing;

	// Revoke local preview blob URL on unmount
	useEffect(() => {
		return () => {
			if (previewBlobUrlRef.current) {
				URL.revokeObjectURL(previewBlobUrlRef.current);
			}
		};
	}, []);

	const setPreviewUrl = (url: string) => {
		if (previewBlobUrlRef.current) {
			URL.revokeObjectURL(previewBlobUrlRef.current);
		}
		previewBlobUrlRef.current = url;
		setImagePreview(url);
	};

	const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
	const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime", "video/ogg"];

	const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		setIsCompressing(true);
		try {
			if (ACCEPTED_VIDEO_TYPES.includes(file.type)) {
				setPreviewUrl(URL.createObjectURL(file));
				setCompressedFile(file);
				setIsCompressing(false);
			} else if (ACCEPTED_IMAGE_TYPES.includes(file.type)) {
				// Show preview immediately, compress in background
				setPreviewUrl(URL.createObjectURL(file));
				const options = {
					maxSizeMB: 2,
					maxWidthOrHeight: 1080,
					useWebWorker: true,
					initialQuality: 0.8,
				};
				const compressed = await imageCompression(file, options);
				setCompressedFile(compressed);
				setIsCompressing(false);
			} else {
				setIsCompressing(false);
			}
		} catch (err) {
			console.error("Media upload failed:", err);
			setIsCompressing(false);
		}
	};

	const onSubmit = (data: any) => {
		const formData = new FormData();
		formData.append("description", data.description);
		if (compressedFile) {
			if (compressedFile.type.startsWith("video/")) {
				formData.append("video", compressedFile);
			} else {
				formData.append("image", compressedFile);
			}
		}

		http
			.post(`/post`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			})
			.then((res) => {
				if (!res.type && compressedFile) {
					res.type = compressedFile.type.startsWith("video/") ? "video" : "image";
				}
				onCreatePost(res);
				closeModal();
			})
			.catch((err) => {
				console.error("Post upload failed:", err);
			});
	};

	const user = useSelector(selectUser) as User;

	return (
		<div className="create-post-modal">
			<div className="create-post-header">
				<UserCard
					userInfo={{ ...user, id: user.userId }}
					darkMode={true}
					onClick={() => {}}
				/>
				{/*
        <div className="privacy-dropdown">
          <span>Pubblica: Chiunque</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 6l4 4 4-4H4z"/>
          </svg>
        </div>
          */}
			</div>

			<form
				className="create-post"
				onSubmit={handleSubmit(onSubmit)}
			>
				<div className="create-post-content">
					<textarea
						{...register("description")}
						placeholder={t("createPost.placeholder")}
						className="create-post-textarea"
						rows={6}
					/>

					{imagePreview && (
						<div className="image-preview">
							{compressedFile?.type.startsWith("video/") ? (
								<video
									src={imagePreview}
									className="preview-image"
									controls
								/>
							) : (
								<img
									src={imagePreview}
									alt={t("createPost.preview")}
									className="preview-image"
								/>
							)}
							<button
								type="button"
								className="remove-image"
								onClick={() => {
									if (previewBlobUrlRef.current) {
										URL.revokeObjectURL(previewBlobUrlRef.current);
										previewBlobUrlRef.current = null;
									}
									setImagePreview(null);
									setCompressedFile(null);
									setIsCompressing(false);
								}}
							>
								×
							</button>
						</div>
					)}
				</div>

				<div className="create-post-toolbar">
					<div className="toolbar-icons">
						<label
							className="toolbar-btn"
							title={t("createPost.media")}
						>
							<input
								type="file"
								id="imageUpload"
								accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime,video/ogg"
								onChange={handleImageChange}
								style={{ display: "none" }}
							/>
							<ImageIcon
								className="toolbar-icon"
								fontSize={20}
							/>
						</label>
					</div>

					<div className="publish-section">
						<Button
							className={`btn btn-primary publish-btn ${
								hasContent ? "btn-active" : "btn-inactive"
							}`}
							type="submit"
							disabled={!hasContent}
						>
							{t("common.buttons.submit")}
						</Button>
					</div>
				</div>
			</form>
		</div>
	);
};

export default CreatePost;
